import { useEffect, useState } from "react";
import { ArrowUpRight, Check, Link2, Sparkles } from "lucide-react";
import { EMBED_KEY } from "@/hooks/useAuth";
import { PageTabs } from "@/components/PageTabs";
import { cn } from "@/lib/utils";
import "@/styles/brand-kit.css";
import "./stories.css";

type Channel = "call" | "email" | "text";

interface Touch {
  day: number;
  ch: Channel;
  missed?: boolean;
  booked?: boolean;
}

interface LeadStory {
  name: string;
  segment: string;
  title: string;
  blurb: string;
  url: string;
  preview: string;
  bookedDay: number;
  touches: Touch[];
}

const CHANNELS: { ch: Channel; label: string }[] = [
  { ch: "call", label: "calls" },
  { ch: "email", label: "emails" },
  { ch: "text", label: "texts" },
];

const dial = (day: number): Touch => ({ day, ch: "call", missed: true });

// ponytail: hardcoded list. Move to Supabase + a composer once stories get added
// often enough that a code change per story hurts.
const STORIES: LeadStory[] = [
  {
    name: "Marcus",
    segment: "Seller lead",
    title: "Ten unanswered dials. One booked appointment.",
    blurb: "A real contact from a live Fello account, masked. Felix kept calling, emailing and texting until he booked a call with the team.",
    url: "https://fello.ai/lead-story",
    preview: "/stories/lead-story-marcus.jpg",
    bookedDay: 133,
    // Every touch up to the booking, from the Fello API export (calls + outreach
    // history), verified 15 Oct 2026. Day 0 = first dial, Jan 28 (Eastern time).
    touches: [
      ...[0, 1, 2, 6, 8, 9, 14, 15, 19, 21].map(dial),
      { day: 21, ch: "call" },
      { day: 27, ch: "call" },
      dial(75),
      dial(133),
      ...[44, 53, 61, 68, 77, 86, 93, 102, 110].map((day): Touch => ({ day, ch: "email" })),
      { day: 71, ch: "text" },
      { day: 84, ch: "text" },
      { day: 133, ch: "text", booked: true },
    ],
  },
];

/** Lead Story tab on felixwins.* (?page=stories): a grid of Felix story pages. */
export default function StoriesApp() {
  useEffect(() => { document.title = "Lead Story · Felix Wins"; }, []);

  return (
    <div className="min-h-screen bg-[#fefbf9]">
      <header className={`${EMBED_KEY ? "" : "sticky top-0"} z-30 bg-white border-b border-[hsl(var(--border))]`}>
        <div className="max-w-7xl mx-auto px-6 h-[60px] flex items-center gap-3">
          {!EMBED_KEY && (
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-[hsl(var(--primary))] text-white grid place-items-center shrink-0">
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="leading-[1.15] min-w-0">
                <div className="text-[16px] font-medium text-[hsl(var(--foreground))] truncate">Felix Wins</div>
                <div className="text-[11px] font-normal text-[hsl(var(--muted-foreground))]/80 truncate mt-0.5">A wall of praise from our clients</div>
              </div>
            </div>
          )}
          <PageTabs active="stories" className={EMBED_KEY ? "" : "ml-2"} />
        </div>
      </header>

      <main className="brand-kit ls-page max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-baseline justify-between gap-4 mb-6">
          <h2 className="ls-h2">All stories</h2>
          <span className="ls-muted">{STORIES.length} {STORIES.length === 1 ? "story" : "stories"}</span>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {STORIES.map((s) => <StoryCard key={s.url} story={s} />)}
        </div>
      </main>
    </div>
  );
}

function StoryCard({ story }: { story: LeadStory }) {
  const count = (ch: Channel) => story.touches.filter((t) => t.ch === ch).length;
  return (
    <article className="ls-card group">
      <div className="ls-card-preview">
        <div className="ls-card-frame">
          <div className="ls-browser-bar"><i /><i /><i /></div>
          <img src={story.preview} alt="" width={720} height={1790} loading="lazy" decoding="async" />
        </div>
      </div>
      <div className="ls-card-body flex flex-col gap-3 flex-1 p-6">
        <p className="ls-overline">{story.name} · {story.segment}</p>
        <h3 className="ls-card-title">{story.title}</h3>
        <p className="ls-card-blurb">{story.blurb}</p>
        <div className="ls-strip" aria-hidden="true">
          {story.touches.map((t, i) => (
            <i
              key={i}
              data-ch={t.ch}
              className={cn(t.missed && "is-missed", t.booked && "is-booked")}
              style={{ left: `${(t.day / story.bookedDay) * 100}%` }}
            />
          ))}
          <span className="ls-strip-sweep" />
        </div>
        <p className="ls-muted">
          {story.bookedDay} days · {CHANNELS.map(({ ch, label }) => `${count(ch)} ${label}`).join(" · ")}
        </p>
        <div className="mt-auto pt-2 flex items-center justify-between gap-3">
          {/* Stretched link: the whole card opens the story, the share button sits above it. */}
          <a href={story.url} target="_blank" rel="noopener noreferrer" className="ls-card-cta ls-card-link">
            Read the story <ArrowUpRight className="w-4 h-4" />
          </a>
          <CopyLinkButton url={story.url} />
        </div>
      </div>
    </article>
  );
}

function CopyLinkButton({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy this link", url);
    }
  };
  return (
    <button type="button" onClick={copy} className="ls-share" aria-live="polite">
      {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
      {copied ? "Link copied" : "Copy link"}
    </button>
  );
}
